const HEX6 = /^#?([0-9a-fA-F]{6})$/;

/**
 * Lighten a 6-digit hex color toward white by `amount` (0–1).
 * Returns the input unchanged when it is not a 6-digit hex string.
 */
export const lighten = (hex: string, amount = 0.4): string => {
  const m = hex.match(HEX6);
  if (!m) {
    return hex;
  }
  const num = parseInt(m[1], 16);
  const r = Math.min(255, Math.round(((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * amount));
  const g = Math.min(255, Math.round(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * amount));
  const b = Math.min(255, Math.round((num & 0xff) + (255 - (num & 0xff)) * amount));
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};

/**
 * Convert a 6-digit hex color to an `rgba()` string with the given alpha.
 * Returns the input unchanged when it is not a 6-digit hex string.
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  const m = hex.match(HEX6);
  if (!m) {
    return hex;
  }
  const num = parseInt(m[1], 16);
  const r = (num >> 16) & 0xff;
  const g = (num >> 8) & 0xff;
  const b = num & 0xff;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/** True when `value` is a `#rrggbb` hex color string (leading `#`, 6 digits). */
export const isHexColor = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value);

/**
 * Lift a brand color until it is legible as text/borders/glows on Raider.IO's
 * dark theme. Computes PERCEPTUAL luma (Rec. 601: 0.299R + 0.587G + 0.114B,
 * normalized 0–1) — a plain channel average overweights blue, so dark blues
 * read as "bright enough" when the eye sees them as dark. While luma sits below
 * `minLum`, lightens toward white in bounded steps. Near-black / dark / muddy
 * picks are raised until readable; colors already above the floor (white, bright
 * green, cyan) are returned untouched. Note pure red (#ff0000) has luma ~0.299 —
 * just under the default 0.3 floor — so it receives one lift step. Returns the
 * input unchanged when it is not a 6-digit hex.
 */
export const legibleOnDark = (hex: string, minLum = 0.3): string => {
  const m = hex.match(HEX6);
  if (!m) {
    return hex;
  }
  let color = `#${m[1]}`;
  // Bounded loop: at +0.2 lightening per step, 6 steps always clears the floor.
  for (let i = 0; i < 6; i++) {
    const num = parseInt(color.slice(1), 16);
    const r = (num >> 16) & 0xff;
    const g = (num >> 8) & 0xff;
    const b = num & 0xff;
    if ((0.299 * r + 0.587 * g + 0.114 * b) / 255 >= minLum) {
      break;
    }
    color = lighten(color, 0.2);
  }
  return color;
};
